import { paramsToFormData } from '../../helpers';
import { emptySplitApi } from './api';
import { createFetchArgs, createFetchArgsWithBody } from '../helpers';
import { endPoints, ETags } from 'src/enums';
import { TFileString } from 'src/global';

export type TShortProvider = {
  id: number;
  type: string;
  name: string;
  avatar: string;
};

export type EditProviderParams = {
  external_client_id: string;
  external_client_secret: string;
  authorization_endpoint: string;
  token_endpoint: string;
  issuer: string;
  userinfo_endpoint: string;
  mapping: string;
  scopes: string;
  redirect_uri: string;
};

export type TOauthProvider = {
  id: string;
  name: string;
  type: ProviderType;
  description?: string;
  avatar: TFileString;
  is_active: boolean;
  is_public: boolean;
  scopes?: string;
  client_id: string;
  issuer: string;
  auto_registration: boolean;
  auth_without_email: boolean;
  password_required: boolean;
  token_endpoint: string;
  external_client_id: string;
  external_client_secret: string;
  authorization_endpoint: string;
  redirect_uri: string;
  userinfo_endpoint: string;
  mapping?: string;
  params: EditProviderParams;
};

export interface IProviderColors {
  button_color: string;
  font_color: string;
}

export interface ILdapParams {
  url: string;
  base: string;
  domain: string;
  mapping: string;
  search_filter: string;
  admin_login: string;
  admin_password: string;
  disable_password_reset: boolean;
  password_error: string;
  provider_colors: IProviderColors;
  provider_title: string;
  show_provider_avatar: boolean;
}

export interface I1CParams {
  url: string;
  admin_login: string;
  admin_password: string;
  mapping: string;
  disable_password_reset: boolean;
  provider_colors: IProviderColors;
  provider_title: string;
  show_provider_avatar: boolean;
}

export interface IIdmParams {
  url: string;
  groups: string;
  mapping: string;
  admin_login: string;
  admin_password: string;
  disable_password_reset: boolean;
  provider_colors: IProviderColors;
  provider_title: string;
  show_provider_avatar: boolean;
}

export interface IEsiaParams {
  issuer: string;
  scopes: string;
  password: string;
  certificate: string;
  redirect_uri: string;
  sign_endpoint: string;
  token_endpoint: string;
  verify_endpoint: string;
  userinfo_endpoint: string;
  external_client_id: string;
  authorization_endpoint: string;
}

export interface IOauthParams {
  issuer: string;
  scopes: string;
  redirect_uri: string;
  token_endpoint: string;
  userinfo_endpoint: string;
  external_client_id: string;
  authorization_endpoint: string;
  external_client_secret: string;
}

export interface IPhoneParams {
  issuer: string;
  external_client_id: string;
  external_client_secret: string;
}

export interface IKloudParams {
  issuer: string;
  external_client_id: string;
  external_client_secret: string;
}

export interface IEmailParams {
  mail_port: string;
  root_mail: string;
  mail_hostname: string;
  mail_password: string;
  mail_code_ttl_sec: string;
}

export interface ISmsParams {
  admin_login: string;
  admin_password: string;
}

export interface IProvider<
  T =
    | ILdapParams
    | I1CParams
    | IIdmParams
    | IOauthParams
    | IEsiaParams
    | IKloudParams
    | IEmailParams
    | IPhoneParams
    | ISmsParams,
> {
  id: string;
  client_id: string;
  type: string;
  is_public: boolean;
  is_active: boolean;
  name: string;
  description?: string;
  avatar: TFileString;
  password_required: boolean;
  params?: T;
}

export enum EGetProviderAction {
  auth = 'auth',
  all = 'all',
}

export enum ProviderType {
  GOOGLE = 'GOOGLE',
  YANDEX = 'YANDEX',
  VK = 'VK',
  MAILRU = 'MAILRU',
  CUSTOM = 'CUSTOM',
  CREDENTIALS = 'CREDENTIALS',
  EMAIL = 'EMAIL',
  ETHEREUM = 'ETHEREUM',
  SMS = 'SMS',
  KLOUD = 'KLOUD',
  OAUTH = 'Oauth2',
  PHONE = 'PHONE',
}

interface IQueryParamsProviders {
  action: EGetProviderAction;
  only_active?: boolean;
  types?: string;
}

interface IProvidersProps {
  client_id: string;
  query: IQueryParamsProviders;
}

interface IQueryIdsProps {
  clientId: string;
  providerId: string;
}

interface IQueryActiveProps {
  clientId: string;
  providers: number[];
}

export const providerApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getProviders: builder.query<IProvider[], IProvidersProps>({
      query: ({ client_id, query }) =>
        createFetchArgs<IQueryParamsProviders>(
          `${endPoints.clients}/${client_id}/${endPoints.providers}`,
          'GET',
          query,
        ),
      providesTags: [ETags.Providers],
    }),

    createProvider: builder.mutation<{ id: string }, { body: IProvider; clientId: string }>({
      // поле avatar не должно отправляться в body
      query: ({ body: { avatar, ...body }, clientId }) => {
        return {
          url: `${endPoints.clients}/${clientId}/${endPoints.providers}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [ETags.Providers],
    }),

    updateAvatar: builder.mutation({
      query: ({
        avatar,
        providerId,
        clientId,
      }: {
        avatar: TFileString;
        providerId: string;
        clientId: string;
      }) => ({
        url: `${endPoints.clients}/${clientId}/${endPoints.providers}/${providerId}/avatar`,
        method: 'PUT',
        body: paramsToFormData('avatar', avatar),
      }),
      invalidatesTags: [ETags.Providers],
    }),

    updateProvider: builder.mutation<void, Partial<IProvider>>({
      query: ({ id, client_id, avatar, is_active, ...body }) => {
        return {
          url: `${endPoints.clients}/${client_id}/${endPoints.providers}/${id}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: [ETags.Providers],
    }),

    activateProviders: builder.mutation<void, IQueryActiveProps>({
      query: ({ clientId, providers }) =>
        createFetchArgsWithBody(
          `${endPoints.clients}/${clientId}/${endPoints.providers}/activate`,
          'PUT',
          { providers },
        ),
      invalidatesTags: [ETags.Providers],
    }),

    deactivateProviders: builder.mutation<void, IQueryActiveProps>({
      query: ({ clientId, providers }) =>
        createFetchArgsWithBody(
          `${endPoints.clients}/${clientId}/${endPoints.providers}/deactivate`,
          'PUT',
          { providers },
        ),
      invalidatesTags: [ETags.Providers],
    }),

    deleteProvider: builder.mutation<void, IQueryIdsProps>({
      query: ({ clientId, providerId }) =>
        createFetchArgs(
          `${endPoints.clients}/${clientId}/${endPoints.providers}/${providerId}`,
          'DELETE',
        ),
      invalidatesTags: [ETags.Providers],
    }),
  }),
});

export const {
  useGetProvidersQuery,
  useCreateProviderMutation,
  useActivateProvidersMutation,
  useDeactivateProvidersMutation,
  useDeleteProviderMutation,
  useUpdateProviderMutation,
  useUpdateAvatarMutation,
} = providerApi;
